/**
 * Pickup time picker - ASAP option + day/time grid based on restaurant/location hours
 */

import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Zap } from 'lucide-react'
import { Button, DividerWithLabel } from '@/components/ui'
import type { RestaurantHours, RestaurantHoursException } from '@/types/menuApi'
import type { PickupInfo } from '@/types/customer'
import { getStoreStatus } from '@/utils/storeHours'
import {
  getEffectiveBusinessTimeZone,
  getTimeZoneAbbreviation,
  getViewerTimeZone,
} from '@/utils/timezone'
import { generateSlots } from '@/utils/pickupSlots'

interface PickupTimePickerProps {
  value: PickupInfo | null
  onChange: (selection: PickupInfo) => void
  disabled?: boolean
  /** API hours (from location or restaurant). If empty/undefined, no time slots shown. */
  hours?: RestaurantHours[]
  hoursExceptions?: RestaurantHoursException[]
  timeZone?: string | null
  lastOrderMinutes?: number
}

export function PickupTimePicker({
  value,
  onChange,
  disabled = false,
  hours,
  hoursExceptions,
  timeZone,
  lastOrderMinutes = 0,
}: PickupTimePickerProps) {
  const { t } = useTranslation()
  const [selectedDay, setSelectedDay] = useState<'today' | 'tomorrow'>(
    value?.pickupTime?.startsWith('Tomorrow') ? 'tomorrow' : 'today'
  )

  const storeStatus = useMemo(
    () => getStoreStatus(hours, { hoursExceptions, businessTimeZone: timeZone }),
    [hours, hoursExceptions, timeZone]
  )
  const viewerTimeZone = getViewerTimeZone()
  const businessTimeZone = getEffectiveBusinessTimeZone(timeZone, undefined)
  const businessAbbr = getTimeZoneAbbreviation(new Date(), businessTimeZone)
  const viewerAbbr = getTimeZoneAbbreviation(new Date(), viewerTimeZone)

  // Pre-compute today's slots to know if the "Today" tab should be visible
  const todaySlots = useMemo(
    () => generateSlots(0, hours, hoursExceptions, timeZone, lastOrderMinutes),
    [hours, hoursExceptions, timeZone, lastOrderMinutes]
  )
  const hasTodaySlots = todaySlots.length > 0

  // Auto-select ASAP on first render if no value yet — only when store is open
  useEffect(() => {
    if (!value && storeStatus.isOpen) {
      onChange({ pickupTime: 'ASAP', scheduledPickupAt: null })
    }
  }, [onChange, storeStatus.isOpen, value])

  // Clear stale ASAP when store has closed — force user to pick a scheduled time
  useEffect(() => {
    if (value?.pickupTime === 'ASAP' && !storeStatus.isOpen) {
      onChange({ pickupTime: '', scheduledPickupAt: null })
    }
  }, [value, storeStatus.isOpen, onChange])

  // Auto-switch to "Tomorrow" tab when store is closed and today has no slots
  useEffect(() => {
    if (!storeStatus.isOpen && !hasTodaySlots && selectedDay === 'today') {
      setSelectedDay('tomorrow')
    }
  }, [storeStatus.isOpen, hasTodaySlots, selectedDay])

  // Sync day tab when value changes externally (e.g. reset)
  useEffect(() => {
    if (value?.pickupTime?.startsWith('Tomorrow')) setSelectedDay('tomorrow')
    else if (value?.pickupTime?.startsWith('Today')) setSelectedDay('today')
  }, [value])

  const slots = useMemo(() => {
    return generateSlots(
      selectedDay === 'today' ? 0 : 1,
      hours,
      hoursExceptions,
      timeZone,
      lastOrderMinutes
    )
  }, [selectedDay, hours, hoursExceptions, timeZone, lastOrderMinutes])

  const isASAP = value?.pickupTime === 'ASAP'
  // Extract time portion: "Today, 2:30 PM" → "2:30 PM"
  const selectedSlot = value?.pickupTime?.replace(/^(Today|Tomorrow), /, '') ?? ''
  const isSlotFromCurrentTab =
    (selectedDay === 'today' && Boolean(value?.pickupTime?.startsWith('Today'))) ||
    (selectedDay === 'tomorrow' && Boolean(value?.pickupTime?.startsWith('Tomorrow')))

  // Available day tabs: hide "Today" when store is closed and no today slots
  const availableDays = useMemo(() => {
    const days: Array<'today' | 'tomorrow'> = []
    if (storeStatus.isOpen || hasTodaySlots) days.push('today')
    days.push('tomorrow')
    return days
  }, [storeStatus.isOpen, hasTodaySlots])

  return (
    <div className="space-y-5">
      {/* ASAP card — only shown when store is currently open */}
      {storeStatus.isOpen && (
        <>
          <Button
            variant="outline"
            className={`w-full flex items-center gap-3 p-4 h-auto rounded-xl text-left justify-start ${
              isASAP ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
            onClick={() => onChange({ pickupTime: 'ASAP', scheduledPickupAt: null })}
            disabled={disabled}
          >
            <div className={`p-2 rounded-full ${isASAP ? 'bg-primary/15' : 'bg-muted'}`}>
              <Zap className={`h-4 w-4 ${isASAP ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <p className={`font-semibold text-sm ${isASAP ? 'text-primary' : ''}`}>
                {t('common:asSoonAsPossible')}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 font-normal">
                {t('common:prepareRightAway')}
              </p>
            </div>
          </Button>

          <DividerWithLabel label={t('common:orChooseATime')} />
        </>
      )}

      {/* Closed banner — shown when store is closed */}
      {!storeStatus.isOpen && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-muted/50">
          <div className="p-2 rounded-full bg-muted">
            <Zap className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="font-semibold text-sm">
              {t(storeStatus.labelKey, storeStatus.labelVars)}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('common:schedulePickupBelow')}
            </p>
          </div>
        </div>
      )}

      {/* Day tabs — only show tabs that have slots or are currently open */}
      <div className="flex gap-2">
        {availableDays.map((day) => (
          <Button
            key={day}
            variant={selectedDay === day ? 'default' : 'secondary'}
            className={`rounded-full px-5 h-11 sm:h-9 text-sm ${
              selectedDay === day
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            onClick={() => setSelectedDay(day)}
            disabled={disabled}
          >
            {day === 'today' ? t('common:today') : t('common:tomorrow')}
          </Button>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        {t('common:timesInRestaurantTime', { abbr: businessAbbr })}
        {businessTimeZone !== viewerTimeZone ? t('common:yourLocalTimezone', { abbr: viewerAbbr }) : ''}
      </p>

      {/* Time slot grid */}
      {slots.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {slots.map((slot) => {
            const isSelected = !isASAP && isSlotFromCurrentTab && selectedSlot === slot.label
            return (
              <Button
                key={slot.scheduledPickupAt}
                variant={isSelected ? 'default' : 'outline'}
                className={`h-11 sm:h-9 rounded-lg text-sm ${
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'hover:border-primary/50 hover:bg-primary/5'
                }`}
                onClick={() => {
                  const dayLabel = selectedDay === 'today' ? 'Today' : 'Tomorrow'
                  onChange({
                    pickupTime: `${dayLabel}, ${slot.label}`,
                    scheduledPickupAt: slot.scheduledPickupAt,
                  })
                }}
                disabled={disabled}
              >
                {slot.label}
              </Button>
            )
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('common:noPickupTimes')}</p>
      )}
    </div>
  )
}
